import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from './contexts/AuthContext';
import Navigation from './components/Navigation';
import LoginScreen from './screens/LoginScreen';
import RegisterScreen from './screens/RegisterScreen';
import GameScreen from './screens/GameScreen';
import ModeSelectScreen from './screens/ModeSelectScreen';
import TrainingScreen from './screens/TrainingScreen';
import ResultsScreen from './screens/ResultsScreen';
import ProfileScreen from './screens/ProfileScreen';
import MultiplayerScreen from './screens/MultiplayerScreen';
import LeaderboardScreen from './screens/LeaderboardScreen';
import MatchHistoryScreen from './screens/MatchHistoryScreen';
import { Play, User } from 'lucide-react';

// Placeholder screens - we'll create these properly later
const HomeScreen = () => {
  const { currentUser } = useAuth();
  const navigate = (to: string) => window.location.href = to;

  const AppContainer = styled.div`
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${({ theme }) => theme.spacing.lg};
    padding-top: 70px;

    @media (max-width: 767px) {
      padding: ${({ theme }) => theme.spacing.md};
      padding-top: 60px;
    }
  `;

  const LogoImage = styled.img`
    width: 100%;
    max-width: 400px;
    height: auto;
    margin-bottom: ${({ theme }) => theme.spacing.lg};

    @media (max-width: 767px) {
      max-width: 300px;
      margin-bottom: ${({ theme }) => theme.spacing.md};
    }
  `;

  const Title = styled.h1`
    color: ${({ theme }) => theme.colors.primary};
    font-size: 3rem;
    margin-bottom: ${({ theme }) => theme.spacing.md};
    text-align: center;
    display: none;

    span {
      color: ${({ theme }) => theme.colors.accent};
    }
  `;

  const Subtitle = styled.p`
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 1.25rem;
    text-align: center;
    max-width: 600px;
    margin-top: ${({ theme }) => theme.spacing.md};

    @media (max-width: 767px) {
      font-size: 1rem;
      margin-top: ${({ theme }) => theme.spacing.sm};
    }
  `;

  const ButtonGroup = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing.md};
    margin-top: ${({ theme }) => theme.spacing.xl};
    flex-wrap: wrap;
    justify-content: center;

    @media (max-width: 767px) {
      margin-top: ${({ theme }) => theme.spacing.lg};
      gap: ${({ theme }) => theme.spacing.sm};
    }
  `;

  const PlayButton = styled.button`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.sm};
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
    min-height: 56px;
    min-width: 180px;
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    color: ${({ theme }) => theme.colors.text};
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    font-size: 1.2rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: ${({ theme }) => theme.shadows.lg};
    }

    &:active {
      transform: scale(0.98);
    }
  `;

  const SecondaryButton = styled(PlayButton)`
    background: ${({ theme }) => theme.colors.surface};
    border: 2px solid ${({ theme }) => theme.colors.primary};
  `;

  return (
    <AppContainer>
      <LogoImage src="/assets/logos/logo-512.png" alt="Bible Duel - Test Your Faith, Challenge The World" />
      <Subtitle>
        Test Your Faith. Challenge The World.
      </Subtitle>
      <ButtonGroup>
        {currentUser ? (
          <>
            <PlayButton onClick={() => navigate('/modes')}>
              <Play size={24} />
              Play Now
            </PlayButton>
            <SecondaryButton onClick={() => navigate('/profile')}>
              <User size={24} />
              Profile
            </SecondaryButton>
          </>
        ) : (
          <>
            <PlayButton onClick={() => navigate('/login')}>
              <Play size={24} />
              Login to Play
            </PlayButton>
            <SecondaryButton onClick={() => navigate('/register')}>
              <User size={24} />
              Sign Up
            </SecondaryButton>
          </>
        )}
      </ButtonGroup>
    </AppContainer>
  );
};

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return currentUser ? children : <Navigate to="/login" replace />;
};

function App() {
  return (
    <>
      {/* Navigation bar - hidden during active gameplay */}
      <Navigation hideOnRoutes={['/game', '/game/multiplayer', '/results']} />

      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomeScreen />} />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/register" element={<RegisterScreen />} />

        {/* Protected Routes */}
        <Route
          path="/modes"
          element={
            <ProtectedRoute>
              <ModeSelectScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/game"
          element={
            <ProtectedRoute>
              <GameScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/game/training"
          element={
            <ProtectedRoute>
              <TrainingScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/game/multiplayer"
          element={
            <ProtectedRoute>
              <MultiplayerScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/results"
          element={
            <ProtectedRoute>
              <ResultsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfileScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leaderboard"
          element={
            <ProtectedRoute>
              <LeaderboardScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/match-history"
          element={
            <ProtectedRoute>
              <MatchHistoryScreen />
            </ProtectedRoute>
          }
        />

        {/* 404 Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
